import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHead from "@/components/PageHead";
import { characters, getCharacterBySlug, getCharacterField } from "@/lib/characters";
import AlliterativeName from "@/components/AlliterativeName";
import { useLocale } from "@/hooks/use-locale";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import JsonLd from "@/components/JsonLd";
import { personSchema, breadcrumbSchema } from "@/lib/seo-schemas";

const CharacterDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const { prefix, currentLang } = useLocale();
  const character = getCharacterBySlug(slug || "");
  const [detailRef, detailVisible] = useScrollAnimation<HTMLElement>();

  if (!character) {
    return (
      <div className="container py-24 text-center">
        <h1 className="text-3xl font-bold mb-4">{t("characterDetail.notFound")}</h1>
        <Link to={`${prefix}/queebi-land-characters`} className="text-accent underline">{t("characterDetail.backToCharacters")}</Link>
      </div>
    );
  }

  const emotionKeywords: Record<string, string> = {
    "liam-lion": "courage for kids, bravery, self-confidence children",
    "fabio-fox": "curiosity learning, creativity for kids, focus and patience",
    "kaleb-koala": "kindness education, reassurance, self-strength children",
    "clyde-crocodile": "patience for children, calm and peaceful, emotional regulation",
    "dana-deer": "empathy development, caring for others, self-care kids",
    "casper-crab": "self-confidence children, shyness, believing in yourself",
  };

  const localizedFullName = getCharacterField(character, "fullName", currentLang);

  const details = [
    { label: t("characterDetail.gender"), value: getCharacterField(character, "gender", currentLang) },
    { label: t("characterDetail.birthday"), value: getCharacterField(character, "birthday", currentLang) },
    { label: t("characterDetail.zodiac"), value: getCharacterField(character, "zodiac", currentLang) },
    { label: t("characterDetail.bloodType"), value: getCharacterField(character, "bloodType", currentLang) },
    { label: t("characterDetail.personality"), value: getCharacterField(character, "personality", currentLang) },
    { label: t("characterDetail.interests"), value: getCharacterField(character, "interests", currentLang) },
    { label: t("characterDetail.favoriteThings"), value: getCharacterField(character, "favoriteThings", currentLang) },
    { label: t("characterDetail.bio"), value: getCharacterField(character, "bio", currentLang) },
  ];

  const personLd = personSchema({
    name: localizedFullName,
    description: getCharacterField(character, "bio", currentLang),
    image: character.image,
    url: `https://queebi.com/${character.slug}`,
  });
  const breadcrumb = breadcrumbSchema([
    { name: "Home", url: "https://queebi.com/" },
    { name: "Characters", url: "https://queebi.com/queebi-land-characters" },
    { name: localizedFullName, url: `https://queebi.com/${character.slug}` },
  ]);

  return (
    <>
      <PageHead
        title={`${localizedFullName} — Queebi Land`}
        description={(() => {
          const bio = getCharacterField(character, "bio", currentLang) || "";
          if (bio.length <= 158) return bio;
          // Trim at last sentence/word break ≤ 158 chars and add ellipsis.
          const cut = bio.slice(0, 155);
          const lastSpace = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("。"), cut.lastIndexOf(" "));
          return (lastSpace > 100 ? cut.slice(0, lastSpace) : cut).trim() + "…";
        })()}

        canonical={`https://queebi.com/${character.slug}`}
        image={`/images/og/characters/${character.slug}-1200x630.jpg`}
        imageAlt={`${character.fullName} — a Queebi Land character`}
        keywords={`Queebi, ${character.fullName}, ${character.name}, ${emotionKeywords[character.slug] || "emotional learning for kids"}`}
      />
      <JsonLd data={[personLd, breadcrumb]} />

      <section
        ref={detailRef}
        className={`py-20 md:py-32 transition-all duration-700 ${detailVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        style={{ backgroundImage: `url(/images/brand/bg-characters.png)`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="container">
          <div className="flex flex-col md:flex-row items-start gap-12 max-w-5xl mx-auto">
            <div className="flex-1 order-2 md:order-1">
              <h1 className="text-3xl md:text-4xl font-bold font-heading mb-10"><AlliterativeName fullName={character.fullName} slug={character.slug} /></h1>
              <div className="space-y-5">
                {details.map((d) => (
                  <div key={d.label} className="flex flex-col sm:flex-row sm:gap-4">
                    <span className="font-bold text-base min-w-[140px] text-foreground/70">{d.label}</span>
                    <span className="text-base text-foreground">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex-1 order-1 md:order-2 flex justify-center">
              <img src={character.image} alt={character.fullName} className="max-w-sm w-full" width={400} height={400} />
            </div>
          </div>

          <div className="flex justify-center gap-4 mt-20 flex-wrap">
            {characters.map((c) => (
              <Link
                key={c.slug}
                to={`${prefix}/${c.slug}`}
                className={`rounded-full p-1 border-2 bg-white transition-all duration-200 hover:scale-110 ${c.slug === slug ? "border-accent scale-110" : "border-transparent hover:border-border"}`}
              >
                <img src={c.menuIcon} alt={c.name} className="h-14 w-14 rounded-full object-cover" width={56} height={56} loading="lazy" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default CharacterDetail;
